import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { RefreshCw, CloudOff } from 'lucide-react'
import { SyncContext } from '../../../contexts/SyncProvider'
import { AuthContext } from '../../../contexts/AuthProvider'
import './SyncStatusIcon.scss'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'

const ICON_SIZE = 14

/**
 * Subtle sync status indicator for the Titlebar.
 *
 * - **idle/synced**: Hidden (nothing to show)
 * - **syncing**: Spinning RefreshCw icon
 * - **error/offline**: CloudOff warning icon
 *
 * Uses useContext() directly instead of useSync()/useAuth() hooks because the
 * Titlebar renders above SyncProvider and AuthProvider in the component tree.
 * When contexts are unavailable, returns null gracefully.
 */
export function SyncStatusIcon() {
  const { t } = useTranslation()
  const syncValue = useContext(SyncContext)
  const authValue = useContext(AuthContext)

  // Contexts not available yet (Titlebar renders above providers)
  if (!syncValue || !authValue) return null
  if (!authValue.isAuthenticated) return null

  const { status } = syncValue.syncState

  if (status === 'syncing') {
    return (
      <WithTooltip content={t('sync.statusIcon.syncing')}>
        <div className="sync-status-icon sync-status-icon--syncing">
          <RefreshCw size={ICON_SIZE} />
        </div>
      </WithTooltip>
    )
  }

  if (status === 'error' || status === 'offline') {
    return (
      <WithTooltip content={t('sync.statusIcon.error')}>
        <div className="sync-status-icon sync-status-icon--error">
          <CloudOff size={ICON_SIZE} />
        </div>
      </WithTooltip>
    )
  }

  return null
}
